import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Image} from 'react-native';
import {
  Text, Appbar, ActivityIndicator, IconButton, Snackbar, Divider,
} from 'react-native-paper';
import DraggableFlatList, {ScaleDecorator} from 'react-native-draggable-flatlist';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const CatalogItemMeasurementsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {catalogItemId, catalogItemName} = route.params;

  const {
    measurementValues, isLoading,
    fetchMeasurementValues, toggleMeasurementByGlobalOption,
    reorderMeasurementValues, clearMeasurementValues,
  } = useMeasurementStore();

  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const loadData = useCallback(async () => {
    await fetchMeasurementValues(catalogItemId);
  }, [catalogItemId, fetchMeasurementValues]);

  useEffect(() => {
    loadData();
    return () => clearMeasurementValues();
  }, [loadData, clearMeasurementValues]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  const handleToggle = useCallback(async (item) => {
    try {
      await toggleMeasurementByGlobalOption(catalogItemId, item.globalOptionId, !item.isEnabled);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [catalogItemId, toggleMeasurementByGlobalOption, t]);

  const handleDragEnd = useCallback(async ({data}) => {
    const assignedItems = data.filter(item => item.isEnabled && item.id);
    if (assignedItems.length === 0) return;

    const orderedIds = assignedItems.map(item => item.id);
    try {
      await reorderMeasurementValues(catalogItemId, orderedIds);
      await loadData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [catalogItemId, reorderMeasurementValues, loadData, t]);

  const renderItem = useCallback(({item, drag, isActive}) => {
    const name = item.globalOptionName || item.customOptionName || '';
    const isAssigned = item.isEnabled;

    return (
      <ScaleDecorator>
        <View style={[styles.row, isActive && styles.rowDragging]}>
          {isAssigned && (
            <IconButton
              icon="drag"
              size={22}
              iconColor={colors.textSecondary}
              onLongPress={drag}
              delayLongPress={150}
              style={styles.dragHandle}
            />
          )}
          {!isAssigned && <View style={styles.dragPlaceholder} />}

          <IconButton
            icon={isAssigned ? 'check-circle' : 'circle-outline'}
            size={24}
            iconColor={isAssigned ? colors.primary : colors.textSecondary}
            onPress={() => handleToggle(item)}
            style={styles.toggleIcon}
          />

          <View style={styles.fieldInfo}>
            <Text style={[styles.fieldName, !isAssigned && styles.fieldNameUnselected]}>
              {name}
            </Text>
          </View>

          {(item.imageUrl1 || item.imageUrl2) && (
            <View style={styles.imageRow}>
              {item.imageUrl1 ? (
                <Image source={{uri: item.imageUrl1}} style={styles.thumbnail} />
              ) : (
                <View style={styles.thumbnailPlaceholder} />
              )}
              {item.imageUrl2 ? (
                <Image source={{uri: item.imageUrl2}} style={styles.thumbnail} />
              ) : (
                <View style={styles.thumbnailPlaceholder} />
              )}
            </View>
          )}
        </View>
      </ScaleDecorator>
    );
  }, [handleToggle]);

  const keyExtractor = useCallback(
    item => `${item.globalOptionId || item.id}`,
    [],
  );
  const ItemSeparator = useCallback(() => <Divider />, []);

  const ListHeader = useCallback(() => (
    <View>
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>
          {t('measurement.measurementsFor', {name: catalogItemName})}
        </Text>
      </View>
      <Divider />
    </View>
  ), [catalogItemName, t]);

  const ListEmpty = useCallback(() => (
    <Text style={styles.emptyText}>{t('common.noData')}</Text>
  ), [t]);

  if (isLoading && measurementValues.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={catalogItemName} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <DraggableFlatList
        data={measurementValues}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onDragEnd={handleDragEnd}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        activationDistance={0}
      />

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  headerRow: {padding: spacing.md, backgroundColor: colors.surface},
  headerTitle: {fontSize: 14, color: colors.textSecondary, lineHeight: 20},
  emptyText: {textAlign: 'center', color: colors.textSecondary, marginTop: spacing.xxl},
  row: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: colors.surface,
    paddingVertical: spacing.xs, paddingRight: spacing.sm,
  },
  rowDragging: {
    backgroundColor: colors.background, elevation: 5,
    shadowColor: '#000', shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25, shadowRadius: 4,
  },
  dragHandle: {margin: 0, marginLeft: spacing.xs},
  dragPlaceholder: {width: 46, marginLeft: spacing.xs},
  toggleIcon: {margin: 0},
  fieldInfo: {flex: 1, marginLeft: spacing.xs},
  fieldName: {fontSize: 16, fontWeight: '600', color: colors.text},
  fieldNameUnselected: {color: colors.textSecondary},
  imageRow: {flexDirection: 'row', gap: 6, marginLeft: spacing.xs},
  thumbnail: {
    width: 50, height: 50, borderRadius: 6,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background,
  },
  thumbnailPlaceholder: {
    width: 50, height: 50, borderRadius: 6,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background,
  },
});

export default CatalogItemMeasurementsScreen;
